import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import { CircularProgress, Grid, Typography, Card, CardContent, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core'
import moment from 'moment'
import localization from 'moment/locale/pt-br'
import { useExameLocalService } from 'hooks/useExameLocalService'
import { Page, Header, SecaoGenerica, ObjetoAvatar } from 'components'
import { Utils } from './utils/utils'
import { useStyles } from './ExameLocal.styles'

export const ExameLocal = (props) => {
  const { route } = props
  moment.locale('pt-br', localization)
  const classes = useStyles()
  const params = useParams()
  const exameLocalService = useExameLocalService()
  const [processandoExameLocal, setProcessandoExameLocal] = useState(true)
  const [exameLocal, setExameLocal] = useState()

  const listarExameLocal = useCallback(async () => {
    await exameLocalService.listarExameLocalComAnalise(params.id, undefined, (dados) => {
      if (dados) setExameLocal({
        ...Utils.formatarExameLocal(dados),
        ...Utils.formatarLocais(dados?.anaLocs),
        ...Utils.formatarVestigios(dados?.vestFixs),
        ...Utils.formatarCadaveres(dados?.anaCads),
      })
      else setExameLocal({})
    }, setProcessandoExameLocal)
  }, [params.id])

  useEffect(() => {
    listarExameLocal()
  }, [listarExameLocal])

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <div className={classes.resultsWrapper}>
        {processandoExameLocal || !exameLocal ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : (<>

          <Card className={classes.headerCard}>
            <CardContent style={{ padding: '24px' }}>
              <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                <Grid item xs={12} sm={12} md={12} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <ObjetoAvatar tipo="EXAME" />
                  <Typography variant="h3" className={classes.caseTitle}>
                    {exameLocal?.numeroExame?.toUpperCase() || 'NÚMERO DO EXAME: -'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider style={{ margin: '20px 0 24px 0' }} />

              <Typography variant="subtitle1" className={classes.caseSubtitle} style={{ marginTop: 0, marginBottom: '24px' }}>
                {exameLocal?.tipoExame?.toUpperCase() || 'EXAME DE LOCAL'}
              </Typography>

              <Grid container spacing={3} className={classes.metaGrid} alignItems="stretch">
                {exameLocal?.horarios?.map((item, idx) => (
                  <Grid key={idx} item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>{item.label}</Typography>
                        <Typography className={classes.metaValue}>
                          {item.value ? moment(item.value).format('LLLL').toUpperCase() : "NÃO INFORMADO"}
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <SecaoGenerica titulo="Atendimento à ocorrência">
            <Grid container spacing={3}>
              {exameLocal?.ocorrencia?.map((item, idx) =>
                (item.value || item.required) && (
                  <Grid key={idx} item xs={item.xs || 12} sm={item.sm || 12} md={item.md || 6}>
                    <Typography className={classes.metaLabel}>{item.label}</Typography>
                    <Typography className={classes.metaValue}>
                      {item.value || "NÃO INFORMADO"}
                    </Typography>
                  </Grid>
                )
              )}
            </Grid>
          </SecaoGenerica>

          {!exameLocal?.locais?.length && (
            <SecaoGenerica titulo="Locais">
              <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                <Typography variant="body2" color="textSecondary">
                  Nenhum local registrado
                </Typography>
              </Grid>
            </SecaoGenerica>
          )}

          {exameLocal?.locais?.map((local, idx) => (
            <SecaoGenerica key={`loc-${idx}`} titulo={`Local ${String(idx + 1).padStart(2, '0')}`}>
              <Grid container spacing={3}>
                {local?.map((item, idx) =>
                  (item.value || item.required) && (
                    <Grid key={idx} item xs={item.xs || 12} sm={item.sm || 12} md={item.md || 6}>
                      <Typography className={classes.metaLabel}>{item.label}</Typography>
                      <Typography className={classes.metaValue}>
                        {item.value || "NÃO INFORMADO"}
                      </Typography>
                    </Grid>
                  )
                )}
              </Grid>
            </SecaoGenerica>
          ))}

          {!exameLocal?.vestigios?.length && (
            <SecaoGenerica titulo="Vestígios">
              <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                <Typography variant="body2" color="textSecondary">
                  Nenhum vestígio registrado
                </Typography>
              </Grid>
            </SecaoGenerica>
          )}

          {exameLocal?.vestigios?.map((vestigio, idx) => (
            <SecaoGenerica key={`ves-${idx}`} titulo={`Vestígio Fixado ${String(idx + 1).padStart(2, '0')}`}>
              <Grid container spacing={3}>
                {vestigio?.map((item, idx) =>
                  (item.value || item.required) && (
                    <Grid key={idx} item xs={item.xs || 12} sm={item.sm || 12} md={item.md || 6}>
                      <Typography className={classes.metaLabel}>{item.label}</Typography>
                      <Typography className={classes.metaValue}>
                        {item.value || "NÃO INFORMADO"}
                      </Typography>
                    </Grid>
                  )
                )}
              </Grid>
            </SecaoGenerica>
          ))}

          {!exameLocal?.cadaveres?.length && (
            <SecaoGenerica titulo="Cadáveres">
              <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                <Typography variant="body2" color="textSecondary">
                  Nenhum cadáver registrado
                </Typography>
              </Grid>
            </SecaoGenerica>
          )}

          {exameLocal?.cadaveres?.map((cadaver, idx) => (
            <SecaoGenerica key={`cad-${idx}`} titulo={`Cadáver ${String(idx + 1).padStart(2, '0')}`}>
              <Grid container spacing={3}>
                {cadaver?.map((item, idx) =>
                  (item.value || item.required) && (
                    <Grid key={idx} item xs={item.xs || 12} sm={item.sm || 12} md={item.md || 6}>
                      <Typography className={classes.metaLabel}>{item.label}</Typography>
                      {!!item.table ? (
                        <TableContainer style={{ marginTop: '5px' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow className={classes.tableHeader}>
                              {item.headers?.map((header, idx) => (
                                <TableCell key={idx} style={{ width: header.minSize }}>
                                  <Typography style={{ fontSize: '13px', fontWeight: 'bold' }}>{header.label}</Typography>
                                </TableCell>
                              ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.values?.map((value, idxr) => (
                                <TableRow key={idxr} className={classes.tableRow} style={{ width: '100%' }}>
                                  {value?.map((v, idxv) => (
                                    <TableCell key={idxv}>
                                      <Typography style={{ fontSize: '13px' }}>{v || "NÃO INFORMADO"}</Typography>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography className={classes.metaValue}>
                          {item.value || "NÃO INFORMADO"}
                        </Typography>
                      )}
                    </Grid>
                  )
                )}
              </Grid>
            </SecaoGenerica>
          ))}


        </>)}
      </div>

    </Page>
  )
}


